import React, { useCallback, useMemo, useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import Swal from 'sweetalert2';
import indianBanks from '../../constants/indianBanks';
import './EmployeeForm.css';

const todayDate = new Date().toISOString().split('T')[0];
const IFSC_PATTERN = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const ACCOUNT_NUMBER_PATTERN = /^\d{6,30}$/;
const DEFAULT_METRO_CITY_KEYWORDS = ['new delhi', 'delhi', 'mumbai', 'kolkata', 'chennai', 'bengaluru', 'bangalore', 'hyderabad', 'pune'];
const normalizeLocationValue = (value) => String(value || '').trim().toLowerCase();

const resolveBenefitCityType = (stateKey, cityName, states, metroCities = []) => {
    const normalizedCity = normalizeLocationValue(cityName);
    const normalizedState = normalizeLocationValue(states?.[stateKey]?.state);

    if (DEFAULT_METRO_CITY_KEYWORDS.some((keyword) => normalizedCity.includes(keyword))) {
        return 1;
    }

    const isMetroCity = metroCities.some((metroCity) => {
        const normalizedMetro = normalizeLocationValue(metroCity);
        return normalizedMetro && (normalizedCity.includes(normalizedMetro) || normalizedMetro.includes(normalizedCity));
    });

    if (isMetroCity) {
        return 1;
    }

    if (normalizedState.includes('delhi')) {
        return 1;
    }

    return 0;
};

const getVisibleBenefitLabels = (labels = [], preferredCityType = 0) => {
    const groupedLabels = new Map();

    labels.forEach((label) => {
        const key = `${label.type || ''}|${label.title || ''}|${label.tax_regime || ''}`;
        if (!groupedLabels.has(key)) {
            groupedLabels.set(key, []);
        }
        groupedLabels.get(key).push(label);
    });

    return Array.from(groupedLabels.values())
        .map((group) => {
            const exactMatch = group.find((label) => Number(label.city_type || 0) === preferredCityType);
            return exactMatch || group.sort((a, b) => Number(b.city_type || 0) - Number(a.city_type || 0))[0];
        })
        .filter(Boolean);
};

function EditEmployee() {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    
    const [formData, setFormData] = useState({
        first_name: '',
        middle_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        gender: '',
        marital_status: '',
        nationality: '',
        birth_date: '',
        fathers_name: '',
        mothers_name: '',
        spouse_name: '',
        address: '',
        state: '',
        city: '',
        pincode: '',
        bank_account_number: '',
        bank_ifsc_code: '',
        bank_name: '',
        bank_branch_name: '',
        department: '',
        designation: '',
        employment_status: '',
        joining_date: '',
        tax_regime: 'Old',
        employee_benefits: []
    });
    
    const [formOptions, setFormOptions] = useState({
        departments: [],
        designations: [],
        states: [],
        countries: [],
        cities: [],
        labels: {
            old_regime: [],
            new_regime: []
        },
        metro_cities: [],
    });
    
    const [errors, setErrors] = useState({});

    const bankFieldStatus = {
        bank_account_number:
            formData.bank_account_number.trim() === ''
                ? null
                : ACCOUNT_NUMBER_PATTERN.test(formData.bank_account_number.trim()),
        bank_ifsc_code:
            formData.bank_ifsc_code.trim() === ''
                ? null
                : IFSC_PATTERN.test(formData.bank_ifsc_code.trim().toUpperCase()),
    };

    const selectedBenefitLabels = useMemo(() => {
        const regimeKey = formData.tax_regime === 'Old' ? 'old_regime' : 'new_regime';
        const preferredCityType = resolveBenefitCityType(
            formData.state,
            formData.city,
            formOptions.states,
            formOptions.metro_cities,
        );

        return getVisibleBenefitLabels(formOptions.labels?.[regimeKey] || [], preferredCityType);
    }, [formData.tax_regime, formData.state, formData.city, formOptions.labels, formOptions.metro_cities, formOptions.states]);
    
    const loadEmployeeData = useCallback(async () => {
        try {
            // First fetch form data (including states)
            const formDataResponse = await api.get('/employees/form-data');
            if (formDataResponse.data.success) {
                const options = formDataResponse.data.data;
                console.log("optionsoptions", options);
                
                setFormOptions(options);
                
                // Then fetch employee data
                const employeeResponse = await api.get(`/employees/${id}`);
                if (employeeResponse.data.success) {
                    const emp = employeeResponse.data.data;
                    
                    // Load cities for the employee's state BEFORE setting form data
                    let cities = [];
                    if (emp.state && options.states && options.states[emp.state]) {
                        cities = options.states[emp.state].cities || [];
                    }
                    
                    // Update form options with cities
                    setFormOptions(prev => ({
                        ...prev,
                        cities: cities
                    }));
                    
                    // Extract employee benefit IDs
                    const benefitIds = emp.emp_benefits 
                        ? emp.emp_benefits.map(benefit => benefit.label_id)
                        : [];
                    
                    // Now set form data with all values including city
                    const newFormData = {
                        first_name: emp.first_name || '',
                        middle_name: emp.middle_name || '',
                        last_name: emp.last_name || '',
                        email: emp.email || '',
                        phone_number: emp.phone_number || '',
                        gender: emp.gender?.toString() || '',
                        marital_status: emp.marital_status?.toString() || '',
                        nationality: emp.nationality || '',
                        birth_date: emp.birth_date || '',
                        fathers_name: emp.fathers_name || '',
                        mothers_name: emp.mothers_name || '',
                        spouse_name: emp.spouse_name || '',
                        address: emp.address || '',
                        state: emp.state?.toString() || '',
                        city: emp.city || '', // This will now work because cities are loaded
                        pincode: emp.pincode || '',
                        bank_account_number: emp.bank_account_number || '',
                        bank_ifsc_code: emp.bank_ifsc_code || '',
                        bank_name: emp.bank_name || '',
                        bank_branch_name: emp.bank_branch_name || '',
                        department: emp.department?.toString() || '',
                        designation: emp.user_designation?.designation_id?.toString() || emp.designation?.toString() || '',
                        employment_status: emp.employment_status?.toString() || '',
                        joining_date: emp.hired_date || '',
                        tax_regime: emp.tax_regime ? (emp.tax_regime.charAt(0).toUpperCase() + emp.tax_regime.slice(1)) : 'Old',
                        employee_benefits: benefitIds
                    };
                    
                    setFormData(newFormData);
                }
            }
        } catch (error) {
            console.error('Failed to fetch employee:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load employee data'
            });
            navigate('/people/list');
        } finally {
            setLoading(false);
        }
    }, [id, navigate]);

    useEffect(() => {
        loadEmployeeData();
    }, [loadEmployeeData]);
    
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let normalizedValue = value;

        if (name === 'bank_ifsc_code') {
            normalizedValue = value.toUpperCase().replace(/\s+/g, '').slice(0, 11);
        }

        if (name === 'bank_account_number') {
            normalizedValue = value.replace(/\s+/g, '');
        }

        setFormData(prev => ({
            ...prev,
            [name]: normalizedValue
        }));
        
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };
    
    const handleStateChange = (e) => {
        const stateId = e.target.value;
        setFormData(prev => ({
            ...prev,
            state: stateId,
            city: ''
        }));
        
        if (stateId && formOptions.states[stateId]) {
            setFormOptions(prev => ({
                ...prev,
                cities: formOptions.states[stateId].cities || []
            }));
        } else {
            setFormOptions(prev => ({
                ...prev,
                cities: []
            }));
        }
    };
    
    const handleBenefitChange = (e) => {
        const value = parseInt(e.target.value);
        const checked = e.target.checked;
        
        setFormData(prev => ({
            ...prev,
            employee_benefits: checked
                ? [...prev.employee_benefits, value]
                : prev.employee_benefits.filter(id => id !== value)
        }));
    };
    
    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
        if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
        if (!formData.email.trim()) newErrors.email = 'Email is required';
        if (!formData.phone_number.trim()) {
            newErrors.phone_number = 'Phone number is required';
        } else if (formData.phone_number.length !== 10) {
            newErrors.phone_number = 'Phone number must be 10 digits';
        }
        if (!formData.gender) newErrors.gender = 'Gender is required';
        if (!formData.marital_status) newErrors.marital_status = 'Marital status is required';
        if (!formData.nationality.trim()) newErrors.nationality = 'Nationality is required';
        if (!formData.birth_date) {
            newErrors.birth_date = 'Birth date is required';
        } else if (formData.birth_date > todayDate) {
            newErrors.birth_date = 'Birth date cannot be in the future';
        }
        if (!formData.address.trim()) newErrors.address = 'Address is required';
        if (!formData.state) newErrors.state = 'State is required';
        if (!formData.city) newErrors.city = 'City is required';
        if (!formData.pincode.trim()) newErrors.pincode = 'Pincode is required';
        if (!formData.bank_account_number.trim()) newErrors.bank_account_number = 'Bank account number is required';
        else if (!ACCOUNT_NUMBER_PATTERN.test(formData.bank_account_number.trim())) newErrors.bank_account_number = 'Bank account number must be 6 to 30 digits';
        if (!formData.bank_ifsc_code.trim()) newErrors.bank_ifsc_code = 'IFSC code is required';
        else if (!IFSC_PATTERN.test(formData.bank_ifsc_code.trim().toUpperCase())) newErrors.bank_ifsc_code = 'Enter a valid IFSC code';
        if (!formData.bank_name.trim()) newErrors.bank_name = 'Bank name is required';
        if (!formData.bank_branch_name.trim()) newErrors.bank_branch_name = 'Branch name is required';
        if (!formData.department) newErrors.department = 'Department is required';
        if (!formData.designation) newErrors.designation = 'Designation is required';
        if (!formData.employment_status) newErrors.employment_status = 'Employment status is required';
        if (!formData.joining_date) {
            newErrors.joining_date = 'Joining date is required';
        } else if (formData.joining_date > todayDate) {
            newErrors.joining_date = 'Joining date cannot be in the future';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Please fill all required fields correctly'
            });
            return;
        }
        
        setSubmitting(true);
        
        try {
            const response = await api.put(`/employees/${id}`, formData);
            
            if (response.data.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: response.data.message,
                    timer: 2000,
                    showConfirmButton: false
                });

                const returnTo = location.state?.returnTo || '/people/list';
                const returnState = location.state?.returnState || undefined;
                navigate(returnTo, returnState ? { state: returnState } : undefined);
            }
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to update employee';
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: message
            });
        } finally {
            setSubmitting(false);
        }
    };
    
    if (loading) {
        return (
            <MainLayout>
                <div className="loading-container">
                    <div className="spinner"></div>
                    <p>Loading employee data...</p>
                </div>
            </MainLayout>
        );
    }
    
    return (
        <MainLayout>
            <div className="employee-form-page">
                <div className="form-card">
                    <h4 className="form-title">Edit Employee</h4>
                    
                    <form onSubmit={handleSubmit}>
                        {/* Personal Information */}
                        <div className="form-section">
                            <h6>Personal Information</h6>
                            
                            <div className="form-row">
                                <div className="form-group">
                                    <label>First Name <span className="required">*</span></label>
                                    <input
                                        type="text"
                                        name="first_name"
                                        className="form-control"
                                        value={formData.first_name}
                                        onChange={handleInputChange}
                                    />
                                    {errors.first_name && <span className="error-text">{errors.first_name}</span>}
                                </div>
                                
                                <div className="form-group">
                                    <label>Middle Name</label>
                                    <input
                                        type="text"
                                        name="middle_name"
                                        className="form-control"
                                        value={formData.middle_name}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                
                                <div className="form-group">
                                    <label>Last Name <span className="required">*</span></label>
                                    <input
                                        type="text"
                                        name="last_name"
                                        className="form-control"
                                        value={formData.last_name}
                                        onChange={handleInputChange}
                                    />
                                    {errors.last_name && <span className="error-text">{errors.last_name}</span>}
                                </div>
                                
                                <div className="form-group">
                                    <label>Email <span className="required">*</span></label>
                                    <input
                                        type="email"
                                        name="email"
                                        className="form-control"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                    />
                                    {errors.email && <span className="error-text">{errors.email}</span>}
                                </div>
                            </div>
                            
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Contact Number <span className="required">*</span></label>
                                    <input
                                        type="text"
                                        name="phone_number"
                                        className="form-control"
                                        value={formData.phone_number}
                                        onChange={handleInputChange}
                                        maxLength="10"
                                    />
                                    {errors.phone_number && <span className="error-text">{errors.phone_number}</span>}
                                </div>
                                
                                <div className="form-group">
                                    <label>Gender <span className="required">*</span></label>
                                    <select
                                        name="gender"
                                        className="form-control"
                                        value={formData.gender}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Select Gender</option>
                                        <option value="0">Male</option>
                                        <option value="1">Female</option>
                                    </select>
                                    {errors.gender && <span className="error-text">{errors.gender}</span>}
                                </div>
                                
                                <div className="form-group">
                                    <label>Marital Status <span className="required">*</span></label>
                                    <select
                                        name="marital_status"
                                        className="form-control"
                                        value={formData.marital_status}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Select Status</option>
                                        <option value="0">Single</option>
                                        <option value="1">Married</option>
                                        <option value="2">Divorced</option>
                                        <option value="3">Widowed</option>
                                    </select>
                                    {errors.marital_status && <span className="error-text">{errors.marital_status}</span>}
                                </div>
                                
                                <div className="form-group">
                                    <label>Nationality <span className="required">*</span></label>
                                    <select
                                        name="nationality"
                                        className="form-control"
                                        value={formData.nationality}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Select Country</option>
                                        {formOptions.countries && formOptions.countries.map((country, idx) => (
                                            <option key={idx} value={country}>{country}</option>
                                        ))}
                                    </select>
                                    {errors.nationality && <span className="error-text">{errors.nationality}</span>}
                                </div>
                            </div>
                            
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Birth Date <span className="required">*</span></label>
                                    <input
                                        type="date"
                                        name="birth_date"
                                        className="form-control"
                                        value={formData.birth_date}
                                        onChange={handleInputChange}
                                        max={todayDate}
                                    />
                                    {errors.birth_date && <span className="error-text">{errors.birth_date}</span>}
                                </div>
                                
                                <div className="form-group">
                                    <label>Father's Name</label>
                                    <input
                                        type="text"
                                        name="fathers_name"
                                        className="form-control"
                                        value={formData.fathers_name}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                
                                <div className="form-group">
                                    <label>Mother's Name</label>
                                    <input
                                        type="text"
                                        name="mothers_name"
                                        className="form-control"
                                        value={formData.mothers_name}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                
                                <div className="form-group">
                                    <label>Spouse Name</label>
                                    <input
                                        type="text"
                                        name="spouse_name"
                                        className="form-control"
                                        value={formData.spouse_name}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>
                        </div>
                        
                        {/* Address Information */}
                        <div className="form-section">
                            <h6>Address Information</h6>
                            
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Address <span className="required">*</span></label>
                                    <input
                                        type="text"
                                        name="address"
                                        className="form-control"
                                        value={formData.address}
                                        onChange={handleInputChange}
                                    />
                                    {errors.address && <span className="error-text">{errors.address}</span>}
                                </div>
                                
                                <div className="form-group">
                                    <label>State <span className="required">*</span></label>
                                    <select
                                        name="state"
                                        className="form-control"
                                        value={formData.state}
                                        onChange={handleStateChange}
                                    >
                                        <option value="">Select State</option>
                                        {formOptions.states && Object.entries(formOptions.states).map(([key, value]) => (
                                            <option key={key} value={key}>{value.state}</option>
                                        ))}
                                    </select>
                                    {errors.state && <span className="error-text">{errors.state}</span>}
                                </div>
                                
                                <div className="form-group">
                                    <label>City <span className="required">*</span></label>
                                    <select
                                        name="city"
                                        className="form-control"
                                        value={formData.city}
                                        onChange={handleInputChange}
                                        disabled={!formData.state}
                                    >
                                        <option value="">Select City</option>
                                        {formOptions?.cities?.map((city, idx) => (
                                            <option key={idx} value={city}>{city}</option>
                                        ))}
                                    </select>
                                    {errors.city && <span className="error-text">{errors.city}</span>}
                                </div>
                                
                                <div className="form-group">
                                    <label>Pincode <span className="required">*</span></label>
                                    <input
                                        type="text"
                                        name="pincode"
                                        className="form-control"
                                        value={formData.pincode}
                                        onChange={handleInputChange}
                                        maxLength="6"
                                    />
                                    {errors.pincode && <span className="error-text">{errors.pincode}</span>}
                                </div>
                            </div>
                        </div>
                        
                        <div className="form-section">
                            <h6>Bank Details</h6>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Bank Account Number <span className="required">*</span></label>
                                    <input
                                        type="text"
                                        name="bank_account_number"
                                        className="form-control"
                                        value={formData.bank_account_number}
                                        onChange={handleInputChange}
                                        inputMode="numeric"
                                        maxLength="30"
                                        style={{
                                            borderColor: bankFieldStatus.bank_account_number === null
                                                ? undefined
                                                : bankFieldStatus.bank_account_number
                                                    ? '#10b981'
                                                    : '#ef4444'
                                        }}
                                    />
                                    {errors.bank_account_number && <span className="error-text">{errors.bank_account_number}</span>}
                                    {!errors.bank_account_number && (
                                        <span
                                            className="helper-text"
                                            style={{
                                                color: bankFieldStatus.bank_account_number === null
                                                    ? '#6b7280'
                                                    : bankFieldStatus.bank_account_number
                                                        ? '#059669'
                                                        : '#dc2626'
                                            }}
                                        >
                                            {bankFieldStatus.bank_account_number === null
                                                ? 'Enter 6 to 30 digits only'
                                                : bankFieldStatus.bank_account_number
                                                    ? 'Valid account number'
                                                    : 'Account number must contain digits only'}
                                        </span>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label>IFSC Code <span className="required">*</span></label>
                                    <input
                                        type="text"
                                        name="bank_ifsc_code"
                                        className="form-control"
                                        value={formData.bank_ifsc_code}
                                        onChange={handleInputChange}
                                        maxLength="11"
                                        style={{ textTransform: 'uppercase' }}
                                    />
                                    {errors.bank_ifsc_code && <span className="error-text">{errors.bank_ifsc_code}</span>}
                                    {!errors.bank_ifsc_code && (
                                        <span
                                            className="helper-text"
                                            style={{
                                                color: bankFieldStatus.bank_ifsc_code === null
                                                    ? '#6b7280'
                                                    : bankFieldStatus.bank_ifsc_code
                                                        ? '#059669'
                                                        : '#dc2626'
                                            }}
                                        >
                                            {bankFieldStatus.bank_ifsc_code === null
                                                ? 'Example: SBIN0001234'
                                                : bankFieldStatus.bank_ifsc_code
                                                    ? 'Valid IFSC code'
                                                    : 'Use format like SBIN0001234'}
                                        </span>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label>Bank Name <span className="required">*</span></label>
                                    <select
                                        name="bank_name"
                                        className="form-control"
                                        value={formData.bank_name}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Select Bank</option>
                                        {formData.bank_name && !indianBanks.includes(formData.bank_name) && (
                                            <option value={formData.bank_name}>{formData.bank_name}</option>
                                        )}
                                        {indianBanks.map((bankName) => (
                                            <option key={bankName} value={bankName}>
                                                {bankName}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.bank_name && <span className="error-text">{errors.bank_name}</span>}
                                </div>

                                <div className="form-group">
                                    <label>Branch Name <span className="required">*</span></label>
                                    <input
                                        type="text"
                                        name="bank_branch_name"
                                        className="form-control"
                                        value={formData.bank_branch_name}
                                        onChange={handleInputChange}
                                    />
                                    {errors.bank_branch_name && <span className="error-text">{errors.bank_branch_name}</span>}
                                </div>
                            </div>
                        </div>

                        {/* Tax Regime & Allowances */}
                        <div className="form-section">
                            <h6>Tax Regime & Allowances</h6>
                            
                            <div className="tax-regime-group">
                                <label>Tax Regime <span className="required">*</span></label>
                                <div className="radio-group">
                                    <label className="radio-label">
                                        <input
                                            type="radio"
                                            name="tax_regime"
                                            value="Old"
                                            checked={formData.tax_regime === 'Old'}
                                            onChange={handleInputChange}
                                        />
                                        Old
                                    </label>
                                    <label className="radio-label">
                                        <input
                                            type="radio"
                                            name="tax_regime"
                                            value="New"
                                            checked={formData.tax_regime === 'New'}
                                            onChange={handleInputChange}
                                        />
                                        New
                                    </label>
                                </div>
                            </div>
                            
                            {selectedBenefitLabels.length > 0 && (
                                <div className="benefits-grid">
                                    {selectedBenefitLabels.map(label => (
                                        <label key={label.id} className="checkbox-label">
                                            <input
                                                type="checkbox"
                                                value={label.id}
                                                checked={formData.employee_benefits.includes(label.id)}
                                                onChange={handleBenefitChange}
                                            />
                                            {label.title}
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                        
                        {/* Employment Details */}
                        <div className="form-section">
                            <h6>Employment Details</h6>
                            
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Department <span className="required">*</span></label>
                                    <select
                                        name="department"
                                        className="form-control"
                                        value={formData.department}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Select Department</option>
                                        {formOptions.departments.map(dept => (
                                            <option key={dept.id} value={dept.id}>{dept.department_name}</option>
                                        ))}
                                    </select>
                                    {errors.department && <span className="error-text">{errors.department}</span>}
                                </div>
                                
                                <div className="form-group">
                                    <label>Designation <span className="required">*</span></label>
                                    <select
                                        name="designation"
                                        className="form-control"
                                        value={formData.designation}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Select Designation</option>
                                        {formOptions.designations.map(desig => (
                                            <option key={desig.id} value={desig.id}>{desig.designation_name}</option>
                                        ))}
                                    </select>
                                    {errors.designation && <span className="error-text">{errors.designation}</span>}
                                </div>
                                
                                <div className="form-group">
                                    <label>Employment Status <span className="required">*</span></label>
                                    <select
                                        name="employment_status"
                                        className="form-control"
                                        value={formData.employment_status}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Select Status</option>
                                        <option value="1">Full Time Permanent</option>
                                        <option value="2">Full Time Contract</option>
                                        <option value="3">Full Time Probation</option>
                                        <option value="4">Part Time Contract</option>
                                        <option value="5">Freelance</option>
                                        <option value="6">Intern</option>
                                    </select>
                                    {errors.employment_status && <span className="error-text">{errors.employment_status}</span>}
                                </div>
                                
                                <div className="form-group">
                                    <label>Joining Date <span className="required">*</span></label>
                                    <input
                                        type="date"
                                        name="joining_date"
                                        className="form-control"
                                        value={formData.joining_date}
                                        max={todayDate}
                                        onChange={handleInputChange}
                                    />
                                    {errors.joining_date && <span className="error-text">{errors.joining_date}</span>}
                                </div>
                            </div>
                        </div>
                        
                        {/* Form Actions */}
                        <div className="form-actions">
                            <button
                                type="button"
                                className="btn-cancel"
                                onClick={() => navigate('/people/list')}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn-submit"
                                disabled={submitting}
                            >
                                {submitting ? 'Updating Employee...' : 'Update Employee'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </MainLayout>
    );
}

export default EditEmployee;
